import { useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { PaymentElement } from '@stripe/react-stripe-js';
import CheckoutAlert from '../CheckoutAlert';
import { buttonClass } from '../CheckoutControls';

interface PaymentStepProps {
  /** False on step 5: the element stays mounted (hidden) so step 5 can confirm it. */
  visible: boolean;
  /** True when a Stripe client secret exists, i.e. this step is inside `<Elements>`. */
  cardReady: boolean;
  /** A 100% coupon left nothing to charge now: no card is needed. */
  noPaymentDue: boolean;
  preparing: boolean;
  prepareError: string | null;
  onRetryPrepare: () => void;
  /** Validates the Payment Element (Stripe's own checks). Resolves an error message, or null. */
  validateCard: () => Promise<string | null>;
  onNext: () => void;
  onBack: () => void;
  /** Set when the charge attempt on step 5 failed, so the member can retry here. */
  failure: { title: string; message: string } | null;
  onDismissFailure: () => void;
}

/**
 * Step 4 — card capture (AR-420). The card is entered in Stripe's Payment
 * Element, an iframe served by Stripe: the number, expiry and CVC never touch
 * AIVIA's page code, network calls or storage. The card is charged on step 5,
 * after the agreement.
 */
export default function PaymentStep({
  visible,
  cardReady,
  noPaymentDue,
  preparing,
  prepareError,
  onRetryPrepare,
  validateCard,
  onNext,
  onBack,
  failure,
  onDismissFailure,
}: PaymentStepProps) {
  const [cardError, setCardError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  const submit = async () => {
    if (noPaymentDue) {
      onDismissFailure();
      onNext();
      return;
    }
    if (!cardReady) return;
    setChecking(true);
    const error = await validateCard().finally(() => setChecking(false));
    if (error) {
      setCardError(error);
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    setCardError(null);
    onDismissFailure();
    onNext();
  };

  return (
    <div hidden={!visible}>
      {failure ? (
        <CheckoutAlert tone="error" title={failure.title}>
          {failure.message} Nothing has been charged. Your login and contact details are saved — just
          update your payment method below.
        </CheckoutAlert>
      ) : null}

      <h2 className="m-0 mb-1.5 text-xl font-bold text-[#4b0a5b] sm:text-[21px]">Payment</h2>
      <p className="mb-5 text-[13px] leading-relaxed text-[#7a6a8c]">
        Enter your payment details to complete your membership. You’ll review the full total before
        anything is charged.
      </p>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {prepareError ? (
          <CheckoutAlert
            tone="error"
            title="We couldn’t set up your payment"
            actions={
              <button type="button" onClick={onRetryPrepare} className={buttonClass('sm')}>
                Try again
              </button>
            }
          >
            {prepareError}
          </CheckoutAlert>
        ) : null}

        {noPaymentDue ? (
          <CheckoutAlert tone="success" title="Nothing to pay today" compact live={false}>
            Your code covers today’s total, so no card is needed now.
          </CheckoutAlert>
        ) : cardReady ? (
          <div className="mb-4" data-testid="stripe-payment-element">
            <PaymentElement options={{ layout: 'tabs' }} />
          </div>
        ) : !prepareError ? (
          <p className="mb-4 text-[13px] text-[#7a6a8c]" role="status">
            {preparing ? 'Preparing secure payment…' : 'Loading payment form…'}
          </p>
        ) : null}

        <p className="mb-4 flex items-center gap-1.5 text-[11.5px] text-[#7a6a8c]">
          <Lock aria-hidden="true" className="h-3.5 w-3.5" />
          Card details are captured by Stripe and never stored on our servers.
        </p>

        {cardError ? (
          <div ref={summaryRef} tabIndex={-1}>
            <CheckoutAlert tone="error" title="Check your card details." compact>
              {cardError}
            </CheckoutAlert>
          </div>
        ) : null}

        <CheckoutAlert tone="info" title="Your progress is saved." compact live={false}>
          Steps 1–3 are complete — you won’t need to re-enter your login or contact information.
        </CheckoutAlert>

        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onBack} className={buttonClass('outline')}>
            Back
          </button>
          <button
            type="submit"
            disabled={checking || (!cardReady && !noPaymentDue)}
            className={buttonClass('primary')}
          >
            {checking ? 'Checking…' : failure ? 'Retry payment' : 'Next'}
          </button>
        </div>
      </form>
    </div>
  );
}
